import { ProductTypeEnum, type Order, type Security } from '@talos/kyoko';
import { isNewOrderSecurity } from './utils';

export interface OrderFormSecurityEligibility {
  allowed: boolean;
  reason?: string;
}

const UNSUPPORTED_PRODUCT_TYPE_MESSAGES: Partial<Record<ProductTypeEnum, string>> = {
  [ProductTypeEnum.Share]: 'Share orders cannot be opened in the Order form',
  [ProductTypeEnum.GovernmentBond]: 'Government bond orders cannot be opened in the Order form',
  [ProductTypeEnum.OTC]: 'OTC / Loan orders cannot be opened in the Order form',
};

const DEFAULT_UNSUPPORTED_MESSAGE = "This order's product type is not supported in the Order form";

function getUnsupportedProductTypeMessage(security: Security): string {
  return UNSUPPORTED_PRODUCT_TYPE_MESSAGES[security.ProductType] ?? DEFAULT_UNSUPPORTED_MESSAGE;
}

/**
 * Whether a security may be loaded into the principal New Order form
 * (modify, resubmit, or prime). Unresolved securities keep prior allow behavior.
 */
export function getOrderFormSecurityEligibility(security: Security | undefined): OrderFormSecurityEligibility {
  if (security == null) {
    return { allowed: true };
  }

  if (isNewOrderSecurity(security)) {
    return { allowed: true };
  }

  return {
    allowed: false,
    reason: getUnsupportedProductTypeMessage(security),
  };
}

export function getOrderFormEligibilityForSymbol(
  symbol: string | undefined,
  securitiesBySymbol: Map<string, Security>
): OrderFormSecurityEligibility {
  if (symbol == null) {
    return { allowed: true };
  }
  return getOrderFormSecurityEligibility(securitiesBySymbol.get(symbol));
}

type OrderResubmitRouting = Pick<Order, 'Symbol'> &
  Partial<Pick<Order, 'ParentOrderID' | 'QuoteID' | 'parentIsCareOrder'>>;

export function resubmitsViaSalesOrderForm(
  order: Partial<Pick<Order, 'ParentOrderID' | 'parentIsCareOrder'>>
): boolean {
  return order.ParentOrderID != null && !order.parentIsCareOrder;
}

export function getOrderFormEligibilityForResubmit(
  order: OrderResubmitRouting,
  securitiesBySymbol: Map<string, Security>
): OrderFormSecurityEligibility {
  if (order.QuoteID != null || resubmitsViaSalesOrderForm(order)) {
    return { allowed: true };
  }
  return getOrderFormEligibilityForSymbol(order.Symbol, securitiesBySymbol);
}
